import {
  type PageStructure,
  type ToolMessage,
  toDragPointerEvent,
  useLinearTransform
} from '@st/document-viewer-kit'
import { clsx } from 'clsx'
import { denormalizeRect } from '@st/geom'
import type { Point } from '@st/geom'
import { useState } from 'react'
import type { FieldToolState } from '~/st-doc-viewer/annotations'
import type { ViewerFieldAnnotation } from '~/st-doc-viewer/viewer-annotations'
import {
  signerFieldAppearance,
  signerFieldAriaLabel,
  signerFieldShape
} from './signer-field-appearance'

export function FieldToolOverlay({
  page,
  toolState,
  activeAnnotation,
  send
}: {
  page: PageStructure
  toolState: FieldToolState
  activeAnnotation: ViewerFieldAnnotation | undefined
  send: (message: ToolMessage<ViewerFieldAnnotation, FieldToolState>) => void
}) {
  const transform = useLinearTransform()
  const [pointer, setPointer] = useState<Point | undefined>()

  const placement = toolState.placement
  if (!placement) return null
  const appearance = signerFieldAppearance(placement.signerRole)
  const previewBounds = pointer
    ? denormalizeRect(fieldBounds(placement.field.type, pointer, page), page.bounds)
    : undefined

  return (
    <>
      <div
        className="absolute inset-0 cursor-crosshair bg-transparent"
        onPointerMove={(event) => {
          if (activeAnnotation) {
            setPointer(undefined)
            return
          }
          setPointer(toDragPointerEvent(event, transform, 'drawing-canvas').point)
        }}
        onPointerLeave={() => setPointer(undefined)}
        onPointerDown={(event) => {
          event.preventDefault()
          event.stopPropagation()

          if (activeAnnotation) {
            setPointer(undefined)
            send({ type: 'deselectAnnotation' })
            return
          }

          const point = toDragPointerEvent(event, transform, 'drawing-canvas').point
          const annotationDraft = {
            mode: 'editing' as const,
            signerRole: placement.signerRole,
            field: placement.field,
            type: 'field' as const,
            pageNumber: page.pageNumber,
            bounds: fieldBounds(placement.field.type, point, page)
          }
          send({ type: 'requestAnnotationDraft', elementStatus: 'selected', annotationDraft })
        }}
      />
      {previewBounds && (
        <div
          className={clsx(appearance.field, signerFieldShape, 'absolute opacity-80')}
          style={{
            pointerEvents: 'none',
            top: previewBounds.y,
            left: previewBounds.x,
            width: previewBounds.width,
            height: previewBounds.height
          }}
        >
          {signerFieldAriaLabel(placement.signerRole, placement.field.type)}
        </div>
      )}
    </>
  )
}

function fieldBounds(
  fieldType: 'signature' | 'date' | 'phone' | 'text',
  point: Point,
  page: PageStructure
) {
  let size = { width: 0.15, height: 0.03 }
  if (fieldType == 'signature') size = { width: 0.22, height: 0.03 }
  const x = (point.x - page.bounds.x) / page.bounds.width
  const y = (point.y - page.bounds.y) / page.bounds.height
  return {
    x: Math.max(0, Math.min(x - size.width / 2, 1 - size.width)),
    y: Math.max(0, Math.min(y - size.height / 2, 1 - size.height)),
    ...size
  }
}
